import React, { useEffect, useState } from "react";
import { BellRing, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { enablePushForSchool, enablePushForAdmin, pushSupported, getPushPermission } from "@/lib/push";
import InstallAppDialog, { isIOS, isStandalone } from "@/components/InstallAppDialog";

const DISMISS_KEY = "amc-push-dismissed-at";
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

/**
 * Compact banner inviting the signed-in user to enable push notifications
 * — or (on iOS) install the PWA first, since iOS requires standalone before push.
 */
export default function PushEnableBanner({ role = "school" }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [denied, setDenied] = useState(false);
  const ios = isIOS();
  const standalone = isStandalone();
  const needsPwaFirst = ios && !standalone;
  const fixed = role === "school"; // escola: lembrete fixo até ativar

  useEffect(() => {
    (async () => {
      // On iOS, show the install prompt even if PushManager is missing
      // (push only becomes available after installing the PWA).
      if (!pushSupported() && !ios) return;
      if (pushSupported()) {
        const perm = await getPushPermission();
        if (perm === "granted") {
          // Já autorizado: garante que a inscrição está salva no servidor.
          (role === "admin" ? enablePushForAdmin : enablePushForSchool)().catch(() => {});
          return;
        }
        if (perm === "denied" && fixed) { setDenied(true); setVisible(true); return; }
        if (perm !== "default") return;
      }
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (!fixed && dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;
      setVisible(true);
    })();
  }, [ios, role, fixed]);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const enable = async () => {
    if (needsPwaFirst) {
      setDialogOpen(true);
      return;
    }
    setBusy(true);
    const fn = role === "admin" ? enablePushForAdmin : enablePushForSchool;
    const r = await fn();
    setBusy(false);
    if (r.ok) {
      toast.success("Notificações ativadas!");
      setVisible(false);
    } else if (r.reason === "denied") {
      toast.error("Permissão bloqueada no navegador");
      if (fixed) setDenied(true); else setVisible(false);
    } else {
      toast.error("Não foi possível ativar");
    }
  };

  return (
    <>
      <div
        className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-3 shadow-sm"
        data-testid="push-enable-banner"
      >
        <div className="w-9 h-9 rounded-full bg-[#7c3aed] text-white flex items-center justify-center shrink-0">
          {needsPwaFirst ? <Smartphone className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-black text-sm text-[#1e3a8a]">
            {needsPwaFirst ? "Instalar app no iPhone" : denied ? "Notificações bloqueadas" : "Ativar notificações"}
          </p>
          <p className="text-[12px] text-slate-600 mt-0.5">
            {denied
              ? "As notificações da AMC estão bloqueadas neste aparelho. Toque no cadeado (🔒) ao lado do endereço do site → Permissões → Notificações → Permitir, e recarregue a página."
              : needsPwaFirst
              ? "Adicione o AMC Escolas à sua tela inicial para receber notificações de novos orçamentos."
              : role === "admin"
                ? "Receba avisos imediatos sobre novos itens, mudanças de status e mensagens — mesmo com o app fechado."
                : "Ative as notificações para receber avisos da AMC, mudanças de status e mensagens — mesmo com o portal fechado."}
          </p>
          <div className="flex gap-2 mt-2">
            {!denied && (
            <Button
              size="sm"
              onClick={enable}
              disabled={busy}
              className="h-8 bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
              data-testid="push-enable-btn"
            >
              {busy ? "Ativando..." : needsPwaFirst ? "Ver como instalar" : "Ativar"}
            </Button>
            )}
            {!fixed && (
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              className="h-8 text-slate-600"
              data-testid="push-dismiss-btn"
            >
              Agora não
            </Button>
            )}
          </div>
        </div>
        {!fixed && (
        <button
          type="button"
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-600 shrink-0"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
        )}
      </div>
      <InstallAppDialog open={dialogOpen} onOpenChange={setDialogOpen} role={role} />
    </>
  );
}
